import { Feather, Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { RouteProp, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useContext, useEffect, useState, memo, useRef } from 'react';
import { FlatList, Text, View } from 'react-native';
import { RectButton, ScrollView, TouchableOpacity } from 'react-native-gesture-handler';
import ReadingText from '../../components/ReadingText';
import { AppContext } from '../../Context/AppContext';
import { Section } from '../../entities/Section';
import { RootStackParamList } from '../../routes/AppStack';
import { getPrologueUseCase } from '../../useCases/GetPrologue';
import { getSectionUseCase } from '../../useCases/GetSection';
import styles from './styles';

type ReadingScreenRouteProp = RouteProp<RootStackParamList, 'Reading'>;
type ReadingScreenNavigationProp = StackNavigationProp<RootStackParamList, 'Reading'>;

type Props = {
  route: ReadingScreenRouteProp;
  navigation: ReadingScreenNavigationProp;
};

const Reading = ({ route, navigation }: Props) => {
  const [section, setSection] = useState<Section|null>(null);
  const [completeSection, setCompleteSection] = useState<Section|null>(null);
  const [lastIndex, setLastIndex] = useState(0);
  const [elementLimit, setElementLimit] = useState(10);
  const [textSize, setTextSize] = useState(23);
  const [iconsSize, setIconsSize] = useState(32);
  const [colorMenuVisible, setColorMenuVisible] = useState(false);

  const myFlatList = useRef<FlatList>(null);

  const {
    bookmark,
    setBookmark,
    saveBookmark,
    getBookmark,
    bgColor,
    setBgColor
  } = useContext(AppContext);

  const { goBack, navigate } = useNavigation();

  const { partIndex, sectionIndex, comeFromHome } = route.params;

  async function loadText() {
    let currentSection;
    
    const isPrologue = partIndex === 0;
    if (isPrologue) {
      currentSection = await getPrologueUseCase.execute();
    } else {
      currentSection = await getSectionUseCase.execute(partIndex, sectionIndex);
    }

    return currentSection;
  }

  async function loadTextApi() {
    const response = await AsyncStorage.getItem(`section_${partIndex}_${sectionIndex}`);

    if (response) {
      const currentSection = JSON.parse(response);
      return currentSection;
    }

    let currentSection;
    
    const isPrologue = partIndex === 0;
    if (isPrologue) {
      currentSection = await getPrologueUseCase.execute();
    } else {
      currentSection = await getSectionUseCase.execute(partIndex, sectionIndex);
    }

    await AsyncStorage.setItem(`section_${partIndex}_${sectionIndex}`, JSON.stringify(currentSection));
    return currentSection;
  }

  function increaseSection() {
    if (completeSection) {
      if (lastIndex >= completeSection.content.length) return;
      const wantedSlice = completeSection.content.slice(lastIndex, lastIndex + elementLimit);
      if (section) {
        const content = section.content.concat(wantedSlice);
        const finalSection = new Section(section.data, content);
        setSection(finalSection);
        setLastIndex(lastIndex + elementLimit);
      }
    }
  }

  useEffect(() => {
    (async () => {
      const color = await AsyncStorage.getItem('bgColor');
      if (color) {
        console.log(color);
        setBgColor(color);
      }

      const sizesJSON = await AsyncStorage.getItem('size');
      if (sizesJSON) {
        const sizes = JSON.parse(sizesJSON);
        setTextSize(sizes.textSize);
        setIconsSize(sizes.iconsSize);
      }

      const gotBookmark = await getBookmark();
      let hasBookmark = false;
      if (gotBookmark !== null) {
        if (
          gotBookmark.partIndex === partIndex
          && gotBookmark.sectionIndex === sectionIndex 
        ) {
          setBookmark(bookmark);
          hasBookmark = true;
        }
      }
      const loadedSection = await loadText();
      
      setCompleteSection(loadedSection);
      
      setSection(loadedSection);
      if (hasBookmark && comeFromHome) {
        setTimeout(() => {
          if (myFlatList.current && bookmark) {
            myFlatList.current.scrollToIndex({
              animated: true,
              index: bookmark.paragraphIndex 
            });
          }
  
        }, 1000);
      } 
    })();
  }, []);

  async function decreaseSize() {
    const newTextSize = textSize - 5;
    const newIconsSize = iconsSize - 5;

    if (newTextSize >= 13 && newTextSize <= 33) {
      setTextSize(newTextSize);
    }

    if (newIconsSize >= 22 && newIconsSize <= 42) {
      setIconsSize(newIconsSize);
    }

    await AsyncStorage.setItem('size', JSON.stringify({
      textSize: newTextSize,
      iconsSize: newIconsSize
    }));
  }
  
  async function increaseSize() {
    const newTextSize = textSize + 5;
    const newIconsSize = iconsSize + 5;
    if (newTextSize >= 13 && newTextSize <= 33) {
      setTextSize(newTextSize);
    }
    
    if (newIconsSize >= 22 && newIconsSize <= 42) {
      setIconsSize(newIconsSize);
    }

    await AsyncStorage.setItem('size', JSON.stringify({
      textSize: newTextSize,
      iconsSize: newIconsSize
    }));
  }

  return (
    <View style={[
      styles.mainContainer,
      {
        backgroundColor: bgColor
      }
    ]}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => goBack()} 
        >
          <Feather name="arrow-left" size={28} />
        </TouchableOpacity>
        <View style={styles.headerRight}>
          <TouchableOpacity
            style={styles.headerButton} 
            onPress={() => navigate('Color')}
          >
            <Ionicons name="color-palette-outline" size={28} />
          </TouchableOpacity> 
          <TouchableOpacity
            style={styles.headerButton}
            onPress={decreaseSize}
            >
            <Text
              style={{
                fontSize: 36,
                fontFamily: 'Montserrat_500Medium'
              }} 
              >
              -
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={increaseSize}
          >
            <Text
              style={{
                fontSize: 36,
                fontFamily: 'Montserrat_500Medium'
              }} 
              >
              +
            </Text>
          </TouchableOpacity>
        </View>
      </View>

        {
          section
          ? 
          <FlatList 
            contentContainerStyle={styles.textContainer}
            data={section.content}
            keyExtractor={(item, index) => 'key'+index}
            ref={myFlatList}
            onScrollToIndexFailed={(error) => {
              const offset = error.averageItemLength * error.index;
              myFlatList.current?.scrollToOffset({ offset });
              setTimeout(() => {
                myFlatList.current?.scrollToIndex({ index: error.index });
              }, 100);
            }}
            renderItem={({ item, index }) => 
              <ReadingText 
                item={item} 
                index={index} 
                partIndex={partIndex} 
                sectionIndex={sectionIndex} 
                sectionName={section.data.title} 
                textSize={textSize}
                iconsSize={iconsSize}
              />
            }
          />
          :
          <Text style={styles.text}>
            Carregando...
          </Text>
        }
    </View>
  );
};

export default Reading;